import { isSameDay } from 'date-fns';
import { createSelector } from 'reselect';

import { PERIOD, START_DATE_FROM, START_DATE_TO } from 'constants/FilterConstants';
import { DATE_TYPE } from 'constants/InterventionConstants';
import { SELF_SERVICE_ORIGIN, SELF_SERVICE_TYPE } from 'constants/SelfServiceConstants';
import { getCurrentUserIsReadOnly } from 'modules/users/selectors/currentUser';
import { getSelectedDealerId } from 'selectors/dealerSelectors';
import { isSelfServiceAnswered } from 'services/InterventionUtils';
import { getProps } from 'utils/selectors';

import { getUIState, getByIds, getById } from '.';
import { filterRequestFormatter } from '../utils';

const getRoot = state => state;

const getState = createSelector(getUIState, state => state.mobility);

const getDataState = createSelector(getState, state => state.data);

const loading = createSelector(getDataState, data => data.loading);

const getIds = createSelector(getDataState, data => data.ids);

export const getFilters = createSelector(getState, ({ filters }) => {
  const toReturn = {
    ...filters,
    [START_DATE_FROM]: filters.period === DATE_TYPE.PLANNED ? new Date().toISOString() : filters[START_DATE_FROM],
    [START_DATE_TO]: filters.period === DATE_TYPE.CURRENT ? new Date().toISOString() : filters[START_DATE_TO],
  };
  return toReturn;
});

export const getPage = createSelector(getDataState, data => data.page);

export const getBaseRequestParams = createSelector(
  getSelectedDealerId,
  getFilters,
  getPage,
  getProps,
  (dealerId, { [PERIOD]: _, ...filters }, { number: page }, excludeFilters) => ({
    page,
    size: 20,
    dealerId,
    paginationType: 'SLICE',
    ...(excludeFilters ? {} : filterRequestFormatter(filters)),
  })
);

const getInterventions = createSelector(getRoot, getIds, getByIds);

/**
 * @param {import('../types/projections/mobility-intervention').MobilityIntervention[]} interventions
 * @returns {Array<{ className: string, } & import('../types/projections/mobility-intervention').MobilityIntervention>}
 */
export const formatInterventions = interventions =>
  interventions.reduce((acc, intervention) => {
    const previous = acc[acc.length - 1];
    const hasBreakDayClass = acc.length > 0 && !isSameDay(previous.startDate, intervention.startDate);

    return [
      ...acc,
      {
        ...intervention,
        className: hasBreakDayClass ? 'break-day' : undefined,
      },
    ];
  }, []);

export const getFormattedInterventions = createSelector(getInterventions, formatInterventions);

/** Mobility modal selectors */
const getModalState = createSelector(getState, state => state.modal);

const getSelectedMobilityModalEntry = createSelector(getModalState, modal => modal.mobility);

export const getSelectedInterventionId = createSelector(
  getSelectedMobilityModalEntry,
  entity => entity?.interventionId
);

export const getModalInterventionDetails = createSelector(
  getRoot,
  getSelectedMobilityModalEntry,
  /** @return {import("../types/projections/mobility-intervention").MobilityIntervention} */
  (state, mobility) => getById(state, mobility.interventionId)
);

export const isMobilityModalOpen = createSelector(getSelectedMobilityModalEntry, mobility => Boolean(mobility));

export const getInitialModalFormValue = createSelector(getSelectedMobilityModalEntry, mobility => ({
  mobility,
  parkingMap: mobility?.parkingMap,
}));

export const getModalSelfService = createSelector(
  getModalInterventionDetails,
  getSelectedMobilityModalEntry,
  (intervention, { id: mobilityId }) => intervention.selfServices.find(({ mobility }) => mobility.id === mobilityId)
);

const getLoadingMobilityState = createSelector(getModalState, state => state.loading);

const isFetchingMobility = createSelector(getLoadingMobilityState, loadingState => loadingState.fetching);

export const isUpdatingMobility = createSelector(getLoadingMobilityState, loadingState => loadingState.updating);

export const isDashboardLoading = createSelector(
  loading,
  isFetchingMobility,
  (fetchingMobilities, fetchingMobility) => fetchingMobilities || fetchingMobility
);

export const getSelectedSelfServiceType = createSelector(getModalSelfService, ({ type }) => type);

export const isMobilityModalDisabled = createSelector(
  isUpdatingMobility,
  getCurrentUserIsReadOnly,
  getModalSelfService,
  (updating, readOnly, selfService) => {
    const skipOrigins = selfService.type === SELF_SERVICE_TYPE.CHECK_IN ? [SELF_SERVICE_ORIGIN.HOME] : [];
    const isAnswered = isSelfServiceAnswered(selfService, skipOrigins);

    return readOnly || updating || isAnswered || Boolean(selfService?.finalInvoice);
  }
);
